import { useCallback, useState } from "react";
import _ from "lodash";

import {
  AccessTimeFilledIcon,
  CancelIcon,
  CheckCircleIcon,
  ClearIcon,
  RemoveCircleIcon,
} from "icons";
import { colors } from "theme/palette";
import { handleInvalidRequest } from "utils/sdk";

import Button from "components/Button";
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/Dialog";
import IconButton from "components/IconButton";
import InputAdornment from "components/InputAdornment";
import Stack from "components/Stack";
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "components/Table";
import Text from "components/Text";
import TextField from "components/TextField";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";
import { useConfirmationDialog } from "components/useConfirmationDialog";

import { useSignedUser } from "entities/Auth/sdk";
import {
  approvedEmailDomainClaim,
  approvedEmailDomainDelete,
  EApprovedEmailDomainStatus,
  IApprovedEmailDomain,
  useApprovedEmailDomains,
} from "entities/Company/sdk";

import { domainSchema } from "./constants";

const ApprovedEmailDomains = () => {
  const [newDomain, setNewDomain] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAdditionDialogOpen, setIsAdditionDialogOpen] = useState(false);

  const { data: user } = useSignedUser();
  const { show: showConfirmationDialog } = useConfirmationDialog();
  const { data: emailDomains, mutate: refetchApprovedEmailDomains } =
    useApprovedEmailDomains();

  const closeAdditionDialog = useCallback(() => {
    setNewDomain("");
    setIsAdditionDialogOpen(false);
  }, []);

  const claimDomain = useCallback(
    ({ domain }: { domain: string }) => {
      if (isSubmitting) {
        return;
      }

      setIsSubmitting(true);

      try {
        domainSchema.validateSync(domain);
      } catch (error) {
        toast.errorMessage((error as Error).message);
        setIsSubmitting(false);
        return;
      }

      approvedEmailDomainClaim({ domain })
        .then(() => {
          toast.successMessage(
            "Domain claimed successfully! If the domain submitted does not match your own domain a review process will be initiated."
          );
          refetchApprovedEmailDomains();

          if (isAdditionDialogOpen) {
            closeAdditionDialog();
          }
        })
        .catch(handleInvalidRequest)
        .finally(() => setIsSubmitting(false));
    },
    [
      refetchApprovedEmailDomains,
      closeAdditionDialog,
      isAdditionDialogOpen,
      isSubmitting,
    ]
  );

  const claimOwnDomain = useCallback(() => {
    if (_.isNil(user)) {
      return;
    }

    claimDomain({ domain: user.email.split("@")[1] });
  }, [user, claimDomain]);

  const deleteDomain = useCallback(
    ({ domain }: { domain: IApprovedEmailDomain }) => {
      approvedEmailDomainDelete({ domainId: domain.id })
        .then(() => {
          toast.successMessage("Domain deleted successfully!");
          refetchApprovedEmailDomains();
        })
        .catch(handleInvalidRequest);
    },
    [refetchApprovedEmailDomains]
  );

  const handleDelete = useCallback(
    ({ domain }: { domain: IApprovedEmailDomain }) => {
      showConfirmationDialog({
        onConfirm: () => deleteDomain({ domain }),
        confirmButtonText: "Delete",
        message: "Are you sure you want to delete this domain?",
      });
    },
    [deleteDomain, showConfirmationDialog]
  );

  const additionDialogSubmit = useCallback(() => {
    claimDomain({ domain: newDomain });
  }, [claimDomain, newDomain]);

  const statusIconRetrieve = useCallback(
    ({ domain }: { domain: IApprovedEmailDomain }) => {
      if (domain.deleted) {
        return (
          <Stack direction="row" gap={1} alignItems="center">
            <CancelIcon
              sx={{ width: "20px", height: "20px", color: colors.red120 }}
            />
            <Text variant="labelSmall" fontWeight={600} color={colors.gray60}>
              Deleted
            </Text>
          </Stack>
        );
      }

      if (domain.status === EApprovedEmailDomainStatus.REJECTED) {
        return (
          <Stack direction="row" gap={1} alignItems="center">
            <RemoveCircleIcon
              sx={{ width: "20px", height: "20px", color: colors.red100 }}
            />
            <Text variant="labelSmall" fontWeight={600} color={colors.gray60}>
              Rejected
            </Text>
          </Stack>
        );
      }

      if (
        domain.status === EApprovedEmailDomainStatus.APPROVED ||
        domain.status === EApprovedEmailDomainStatus.AUTOMATICALLY_APPROVED
      ) {
        return (
          <Stack direction="row" gap={1} alignItems="center">
            <CheckCircleIcon
              sx={{ width: "20px", height: "20px", color: colors.green100 }}
            />
            <Text variant="labelSmall" fontWeight={600} color={colors.gray60}>
              Approved
            </Text>
          </Stack>
        );
      }

      if (domain.status === EApprovedEmailDomainStatus.PENDING) {
        return (
          <Stack direction="row" gap={1} alignItems="center">
            <AccessTimeFilledIcon
              sx={{ width: "20px", height: "20px", color: colors.yellow100 }}
            />
            <Text variant="labelSmall" fontWeight={600} color={colors.gray60}>
              Waiting for approval
            </Text>
          </Stack>
        );
      }
    },
    []
  );

  return (
    <Stack>
      <Text variant="text1" fontWeight={700} color={colors.gray100}>
        Approved email domains
      </Text>

      <Text
        variant="text3"
        marginTop={1}
        marginBottom={3}
        color={colors.gray80}
      >
        This functionality lets you claim an email domain as your company's own.
        This means that any new user signing up with an email from that domain
        will automatically be included as part of your company on Builders
        Patch.
        <br />
        <br />
        {user?.is_admin && (
          <>
            You can automatically claim your current user email domain(
            <b>@{user.email.split("@")[1]}</b>) by clicking{" "}
            <Text
              color={colors.blue100}
              display="inline"
              variant="text3"
              component="span"
              sx={{ cursor: "pointer" }}
              onClick={claimOwnDomain}
            >
              here
            </Text>
            .
          </>
        )}
      </Text>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <Text variant="labelSmall">domain</Text>
            </TableCell>
            <TableCell>
              <Text variant="labelSmall">status</Text>
            </TableCell>
            {user?.is_admin && <TableCell sx={{ width: "30px" }}></TableCell>}
          </TableRow>
        </TableHead>

        <TableBody>
          {emailDomains?.map((domain) => (
            <TableRow
              key={domain.id}
              sx={{
                height: "58px",
                transition: "opacity 0.2s linear",
                opacity: domain.deleted ? 0.3 : 1,
              }}
            >
              <TableCell>@{domain.domain}</TableCell>
              <TableCell>{statusIconRetrieve({ domain })}</TableCell>
              {user?.is_admin && (
                <TableCell sx={{ textAlign: "right", width: "30px" }}>
                  {!domain.deleted && (
                    <Tooltip title="Delete domain">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete({ domain })}
                      >
                        <ClearIcon sx={{ width: "20px", height: "20px" }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
          {_.isEmpty(emailDomains) && (
            <TableRow>
              <TableCell colSpan={3}>
                <Text variant="text4">
                  No email domains have been claimed yet.
                </Text>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {user?.is_admin && user.company_is_paying_customer && (
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          onClick={() => setIsAdditionDialogOpen(true)}
          sx={{ alignSelf: "flex-start", marginTop: 2 }}
        >
          + Add domain
        </Button>
      )}

      <Dialog
        fullWidth
        maxWidth="md"
        open={isAdditionDialogOpen}
        onClose={closeAdditionDialog}
      >
        <DialogTitle
          sx={{
            margin: 2,
            borderRadius: "8px",
            backgroundColor: colors.blue20,
          }}
        >
          <Text variant="h1" marginBottom={1} color={colors.gray100}>
            + Add new approved domain
          </Text>
          <Text variant="text3" color={colors.gray80}>
            Setting up an approved email domain allows any new user signing up
            with an email from that domain to be automatically included as part
            of your company on Builders Patch. Domain extensions must be unique
            to your organization. You cannot use public domains like @gmail.com
            etc.
          </Text>
        </DialogTitle>
        <DialogContent sx={{ padding: 2, overflowY: "visible" }}>
          <TextField
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">@</InputAdornment>
              ),
            }}
            placeholder="Enter email domain"
            sx={{ width: "100%" }}
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button variant="text" onClick={closeAdditionDialog}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            disabled={newDomain === "" || isSubmitting}
            onClick={additionDialogSubmit}
          >
            Add domain
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ApprovedEmailDomains;
